import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../lib/auth-fetch';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import {
  FileSignature, Search, Tag, Layers, ChevronRight, Trash2, Printer, Loader2,
  FileDown, ClipboardCheck, AlertTriangle,
} from 'lucide-react';
import OrderDocumentEditor from './OrderDocumentEditor';

export default function DocumentArchivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [openDocId, setOpenDocId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [msg, setMsg] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-Link: ?doc=<id> öffnet Editor direkt — wird vom QR-Code im PDF genutzt.
  useEffect(() => {
    const deepLinkDoc = searchParams.get('doc');
    if (deepLinkDoc && !openDocId) setOpenDocId(deepLinkDoc);
  }, [searchParams, openDocId]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`/api/order-documents/archive${q ? '?q=' + encodeURIComponent(q) : ''}`);
    if (r.ok) { const d = await r.json(); setList(d.documents || []); }
    setLoading(false);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(s => s.size === list.length ? new Set() : new Set(list.map(d => d.id)));

  const deleteOne = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Dieses Dokument aus dem Archiv löschen? Die Eingaben gehen verloren.')) return;
    const r = await authFetch(`/api/order-documents/${id}`, { method: 'DELETE' });
    if (r.ok) { setMsg('✓ Gelöscht'); setTimeout(() => setMsg(''), 1500); load(); }
    else alert('Löschen fehlgeschlagen');
  };

  const downloadPdf = async (d, e) => {
    e.stopPropagation();
    const r = await authFetch(`/api/order-documents/${d.id}/pdf?download=true`);
    if (!r.ok) { alert('PDF konnte nicht erzeugt werden'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const num = d.easybillDocumentNumber || 'dokument';
    const sys = (d.systemName || 'system').replace(/[^\w-]/g, '_');
    a.href = url; a.download = `Begleitdokument_${num}_${sys}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // 04.02.2026 (User-Umzug): Systemcheckliste gehört direkt zum Auftrag.
  // Der auftrags-gebundene Endpoint liefert das gespeicherte PDF — und
  // ist auf `order_documents.read` beschränkt (nicht Customer-Rolle).
  // 18.06.26 (User-Wunsch): „Aufmachen" = PDF in neuem Browser-Tab anzeigen,
  // zusätzlich separater Download daneben.
  const _fetchChecklistBlob = async (d) => {
    const url = d.systemChecklist?.downloadUrl;
    if (!url) return null;
    const r = await authFetch(url);
    if (!r.ok) { alert('Systemcheckliste konnte nicht geladen werden'); return null; }
    return await r.blob();
  };
  const openSystemChecklist = async (d, e) => {
    e.stopPropagation();
    const blob = await _fetchChecklistBlob(d);
    if (!blob) return;
    const objUrl = URL.createObjectURL(blob);
    window.open(objUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  };
  const downloadSystemChecklist = async (d, e) => {
    e.stopPropagation();
    const blob = await _fetchChecklistBlob(d);
    if (!blob) return;
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = d.systemChecklist.fileName
      || `Systemcheckliste_${d.easybillDocumentNumber || 'auftrag'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`${selected.size} Dokument(e) wirklich aus dem Archiv löschen?`)) return;
    await Promise.all(Array.from(selected).map(id =>
      authFetch(`/api/order-documents/${id}`, { method: 'DELETE' })));
    setSelected(new Set());
    setMsg(`✓ ${selected.size} Dokumente gelöscht`);
    setTimeout(() => setMsg(''), 2000);
    load();
  };

  const bulkPrint = async () => {
    if (!selected.size) return;
    setBulkPrinting(true);
    try {
      const docs = await Promise.all(
        Array.from(selected).map(async id => {
          const r = await authFetch(`/api/order-documents/${id}`);
          if (r.ok) return await r.json();
          return null;
        }),
      );
      const valid = docs.filter(Boolean);
      if (!valid.length) { alert('Keine Dokumente geladen'); return; }

      const w = window.open('', '_blank');
      if (!w) { alert('Popup blockiert – bitte Popups erlauben'); return; }
      w.document.write(renderBulkHtml(valid));
      w.document.close();
      setTimeout(() => { w.focus(); w.print(); }, 400);
    } finally {
      setBulkPrinting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2 mb-1" data-testid="archive-title">
        <FileSignature className="w-5 h-5 text-[#9ec043]" /> Dokumenten-Archiv
      </h1>
      <p className="text-xs text-gray-400 mb-4">Alle erstellten Produktbegleitdokumente — durchsuchbar nach Auftragsnummer, Kommission, Kunde und Seriennummer.</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <Input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Seriennummer / Auftragsnummer / Kunde suchen…"
            className="pl-9 text-sm h-10" data-testid="archive-search" autoFocus />
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-2 bg-[#9ec043]/10 border border-[#9ec043]/30 rounded-lg px-3 py-1.5" data-testid="archive-bulk-bar">
            <span className="text-xs text-[#32373c] font-medium">{selected.size} ausgewählt</span>
            <Button onClick={bulkPrint} disabled={bulkPrinting}
              className="h-7 text-xs bg-[#9ec043] hover:bg-[#8aad38] text-white"
              data-testid="btn-bulk-print">
              {bulkPrinting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Printer className="w-3 h-3 mr-1" />}
              Als PDF drucken
            </Button>
            {isAdmin && (
              <Button onClick={bulkDelete} variant="outline" className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50" data-testid="btn-bulk-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Löschen
              </Button>
            )}
            <button onClick={() => setSelected(new Set())} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
          </div>
        )}
        {msg && <span className="text-xs text-green-600" data-testid="archive-msg">{msg}</span>}
      </div>

      {list.length > 0 && (
        <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
          <Checkbox checked={selected.size === list.length && list.length > 0}
            onCheckedChange={toggleAll}
            data-testid="archive-select-all" />
          <span>Alle auswählen ({list.length})</span>
        </div>
      )}

      {loading ? <div className="text-xs text-gray-400">Lade…</div> : list.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-400" data-testid="archive-empty">
          {q ? `Keine Treffer für „${q}".` : 'Noch keine Dokumente im Archiv.'}
        </div>
      ) : (
        <div className="space-y-2" data-testid="archive-list">
          {list.map(d => (
            <div key={d.id}
              className={`bg-white border rounded-xl p-4 hover:border-[#9ec043] hover:shadow-sm transition-all flex items-start gap-3 cursor-pointer ${selected.has(d.id) ? 'border-[#9ec043] bg-[#9ec043]/5' : 'border-gray-200'}`}
              onClick={() => setOpenDocId(d.id)}
              data-testid={`archive-row-${d.id}`}>
              <div onClick={e => e.stopPropagation()} className="pt-1">
                <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleSel(d.id)}
                  data-testid={`archive-check-${d.id}`} />
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#9ec043]/10 text-[#9ec043] flex items-center justify-center shrink-0">
                <Layers className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#32373c]">{d.systemName || 'Ohne System'}</span>
                  {d.easybillDocumentNumber && <span className="text-xs text-gray-500">· Auftrag {d.easybillDocumentNumber}</span>}
                  {d.kommission && <span className="text-xs text-[#9ec043] font-medium truncate max-w-md">· {d.kommission}</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {d.customerName || '—'}{d.easybillDocumentDate ? ` · ${d.easybillDocumentDate}` : ''} · zuletzt bearbeitet {d.updatedAt ? new Date(d.updatedAt).toLocaleString('de-DE') : '—'}
                  {!d.orderExists && <span className="ml-2 text-[10px] text-amber-600 italic">· Original-Auftrag nicht mehr vorhanden</span>}
                </div>
                {d.serialsPreview?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {d.serialsPreview.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 font-mono">
                        <Tag className="w-2.5 h-2.5" /> {s}
                      </span>
                    ))}
                    {d.serialCount > 5 && <span className="text-[10px] text-gray-400">+ {d.serialCount - 5} weitere</span>}
                  </div>
                )}
              </div>
              {isAdmin && (
                <button onClick={(e) => deleteOne(d.id, e)}
                  className="p-1.5 text-gray-300 hover:text-red-500 shrink-0"
                  title="Dokument löschen"
                  data-testid={`btn-archive-del-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              {d.systemChecklist?.available && (
                <div className="flex items-center shrink-0">
                  <button
                    onClick={(e) => openSystemChecklist(d, e)}
                    className="p-1.5 text-[#9ec043] hover:text-[#6d8a2e] inline-flex items-center gap-1"
                    title={
                      d.systemChecklist.forceCompleted
                        ? 'Systemcheckliste (unvollständig abgeschlossen) — im neuen Tab ansehen'
                        : `Systemcheckliste · ${d.systemChecklist.generatedAt?.slice(0, 10) || ''} — im neuen Tab ansehen`
                    }
                    data-testid={`btn-archive-sc-view-${d.id}`}
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    {d.systemChecklist.forceCompleted && (
                      <AlertTriangle className="w-3 h-3 text-amber-600" />
                    )}
                  </button>
                  <button
                    onClick={(e) => downloadSystemChecklist(d, e)}
                    className="p-1.5 text-gray-300 hover:text-[#9ec043]"
                    title="Systemcheckliste herunterladen"
                    data-testid={`btn-archive-sc-dl-${d.id}`}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <button onClick={(e) => downloadPdf(d, e)}
                className="p-1.5 text-gray-300 hover:text-[#9ec043] shrink-0"
                title="PDF herunterladen"
                data-testid={`btn-archive-pdf-${d.id}`}>
                <FileDown className="w-4 h-4" />
              </button>
              <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 mt-1" />
            </div>
          ))}
        </div>
      )}

      {openDocId && <OrderDocumentEditor docId={openDocId} onClose={() => {
        setOpenDocId(null);
        if (searchParams.get('doc')) {
          const sp = new URLSearchParams(searchParams);
          sp.delete('doc');
          setSearchParams(sp, { replace: true });
        }
        load();
      }} />}
    </div>
  );
}

function renderBulkHtml(docBundles) {
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const pagesHtml = docBundles.map(({ document: doc, template, order }, idx) => {
    const values = doc.values || {};
    const headerCells = (template.header || []).map(h => {
      const v = values.header?.[h.key] || '';
      return `<div class="hcell"><div class="hlabel">${esc(h.label)}</div><div class="hvalue">${esc(v)}</div></div>`;
    }).join('');

    const sectionsHtml = (template.sections || []).map(sec => {
      const vv = values.sections?.[sec.id];
      if (sec.type === 'textarea') {
        if (!vv) return '';
        return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="note">${esc(vv)}</div></div>`;
      }
      if (sec.type === 'auto_product') {
        const v = vv || {};
        if (!v.systemName) return '';
        return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="product">${esc(v.systemName)}${v.quantity > 1 ? ` <span class="qty">× ${v.quantity}</span>` : ''}</div></div>`;
      }
      if (sec.type === 'auto_component') {
        const rows = vv || [];
        if (!rows.length) return '';
        return `<div class="sec"><h3>${esc(sec.label)} (${rows.length})</h3>
          <table><thead><tr><th style="width:28px">#</th><th>Modell</th><th>Seriennummer</th></tr></thead>
          <tbody>${rows.map((r, i) => `<tr><td class="num">${i + 1}</td><td>${esc(r.modell)}${r.hersteller ? ` <span class="sub">${esc(r.hersteller)}</span>` : ''}</td><td class="ser">${esc(r.serial || '')}</td></tr>`).join('')}</tbody></table></div>`;
      }
      if (sec.type === 'select') {
        const rows = vv || [];
        const on = rows.filter(r => r.checked);
        if (!on.length) return '';
        return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="chips">${on.map(r => `<span class="chip on">☑ ${esc(r.option)}</span>`).join('')}</div></div>`;
      }
      if (sec.type === 'select_serial') {
        const rows = (vv || []).filter(r => r.checked || r.serial);
        if (!rows.length) return '';
        return `<div class="sec"><h3>${esc(sec.label)}</h3>
          <table><thead><tr><th style="width:28px"></th><th>Option</th><th>Seriennummer</th></tr></thead>
          <tbody>${rows.map(r => `<tr><td class="ck">${r.checked ? '☑' : '☐'}</td><td>${esc(r.option)}</td><td class="ser">${esc(r.serial || '')}</td></tr>`).join('')}</tbody></table></div>`;
      }
      if (sec.type === 'inventory_pick') {
        const v = vv || {};
        const items = v.items || [];
        if (!items.length && !v.notes) return '';
        const tableHtml = items.length ? `
          <table><thead><tr><th>Artikel</th><th style="width:60px">Anzahl</th><th>Seriennummer</th></tr></thead>
          <tbody>${items.map(it => `<tr><td>${esc(it.hersteller ? it.hersteller + ' · ' : '')}${esc(it.modell)}</td><td class="num">${esc(it.anzahl ?? '')}</td><td class="ser">${esc(it.serial || '')}</td></tr>`).join('')}</tbody></table>` : '';
        const notesHtml = v.notes ? `<div class="note">${esc(v.notes)}</div>` : '';
        return `<div class="sec"><h3>${esc(sec.label)}</h3>${tableHtml}${notesHtml}</div>`;
      }
      return '';
    }).join('');

    return `<section class="page" ${idx > 0 ? 'style="page-break-before: always;"' : ''}>
      <h1>${esc(template.title || 'Produktbegleitdokument')}</h1>
      <div class="meta">Auftrag <strong>${esc((order || {}).easybillDocumentNumber || '')}</strong>
        · ${esc((order || {}).customerCompany || (order || {}).customerName || '')}
        ${doc.kommission ? ` · <strong>${esc(doc.kommission)}</strong>` : ''}
        ${(order || {}).easybillDocumentDate ? ` · ${esc((order || {}).easybillDocumentDate)}` : ''}
      </div>
      <div class="hgrid">${headerCells}</div>
      ${sectionsHtml}
      <div class="sig">
        <div class="sig-box">Unterschrift Monteur</div>
        <div class="sig-box">Unterschrift Kunde / Datum</div>
      </div>
      <div class="fob">Blatt ${idx + 1} / ${docBundles.length} · EES Effiziente Energiespeichersysteme GmbH · erstellt am ${new Date().toLocaleString('de-DE')}</div>
    </section>`;
  }).join('');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Begleitdokumente (${docBundles.length})</title>
  <style>
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1d2229; font-size: 11pt; margin: 0; }
    .page { padding: 0; }
    h1 { font-size: 16pt; margin: 0 0 2mm; color: #32373c; }
    h3 { font-size: 11pt; margin: 3mm 0 1.5mm; color: #32373c; border-bottom: 0.3mm solid #9ec043; padding-bottom: 1mm; }
    .meta { color: #5b6470; font-size: 9pt; margin-bottom: 5mm; }
    .product { font-size: 16pt; font-weight: 700; color: #9ec043; padding: 3mm 0; }
    .product .qty { font-size: 12pt; color: #5b6470; font-weight: 400; }
    .hgrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3mm; margin-bottom: 5mm; }
    .hcell { border: 0.3mm solid #ccc; padding: 1.5mm 2mm; border-radius: 1mm; }
    .hlabel { font-size: 7pt; letter-spacing: 0.5px; color: #7a8490; text-transform: uppercase; }
    .hvalue { font-size: 10pt; color: #1d2229; min-height: 5mm; }
    .sec { margin-bottom: 4mm; page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
    th { text-align: left; padding: 1mm 1.5mm; background: #f5f6f7; border-bottom: 0.2mm solid #ccc; font-weight: 600; font-size: 8.5pt; color: #5b6470; text-transform: uppercase; letter-spacing: 0.5px; }
    td { padding: 1mm 1.5mm; border-bottom: 0.2mm solid #eee; }
    .ck { width: 6mm; text-align: center; font-size: 12pt; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .ser { font-family: 'Courier New', monospace; color: #32373c; }
    .sub { font-size: 8pt; color: #7a8490; }
    .chips { display: flex; flex-wrap: wrap; gap: 2mm; }
    .chip { border: 0.2mm solid #9ec043; color: #32373c; padding: 0.5mm 2mm; border-radius: 3mm; }
    .note { border: 0.2mm solid #ccc; padding: 2mm; min-height: 8mm; border-radius: 1mm; white-space: pre-wrap; font-size: 9.5pt; }
    .sig { margin-top: 8mm; display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; page-break-inside: avoid; }
    .sig-box { border-top: 0.2mm solid #32373c; padding-top: 1mm; font-size: 8.5pt; color: #5b6470; min-height: 14mm; }
    .fob { text-align: right; font-size: 7.5pt; color: #9aa3ac; margin-top: 6mm; }
  </style></head><body>
    ${pagesHtml}
  </body></html>`;
}
